import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCurrentUser, fetchAuthSession } from '@aws-amplify/auth';
import { useUser } from '../utils/hooks/useUser';

const Results = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const submissionId = searchParams.get('submission_id'); // This is our submission_id
  const { userId, userEmail, isLoading: userLoading } = useUser();
  const [status, setStatus] = useState('PROCESSING');
  const [error, setError] = useState(null);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [resultData, setResultData] = useState({
    status: 'PROCESSING',
    fileKey: ''
  });
  const [polishedContent, setPolishedContent] = useState(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false); // Add loading state for API calls
  const [isCheckingDocumentStatus, setIsCheckingDocumentStatus] = useState(false); // Separate loading state for document creation
  const [stepProgress, setStepProgress] = useState({
    'UPLOADED': 0,
    'ANALYZE JOB DESCRIPTION': 0,
    'ANALYZE RESUME': 0,
    'GENERATE CONTENT': 0,
    'POLISH CONTENT': 0,
    'PREVIEW CONTENT': 0
  });

  // Define the order of steps (removed CREATE DOCUMENT since workflow ends at PREVIEW CONTENT)
  const steps = [
    'UPLOADED',
    'ANALYZE JOB DESCRIPTION',
    'ANALYZE RESUME',
    'GENERATE CONTENT',
    'POLISH CONTENT',
    'PREVIEW CONTENT'
  ];

  // Mapping from backend status to step index (updated for new workflow)
  const statusToStepMapping = {
    'UPLOADED': 0,
    'ANALYZE JOB DESCRIPTION': 1,
    'ANALYZE RESUME': 2,
    'GENERATE CONTENT': 3,
    'POLISH CONTENT': 4,
    'PREVIEW CONTENT': 5,
    'COMPLETED': 5
  };

  // Get authenticated user's email
  useEffect(() => {
    const getUser = async () => {
      try {
        const { username, signInDetails } = await getCurrentUser();
        const session = await fetchAuthSession();
        
        const idToken = session.tokens.idToken;
        const email = idToken.payload.email;
        
        if (!email) {
          throw new Error('Email not found in user session');
        }
        
        console.log('Authenticated user email:', email);
        setLoading(false);
        
      } catch (error) {
        console.error('Auth error:', error);
        setError('Please login to view results');
        setLoading(false);
        navigate('/login');
      }
    };
    getUser();
  }, [navigate]);

  useEffect(() => {
    // Reset status and progress when component mounts or userEmail changes (new submission)
    setStatus('UPLOADED');
    setPolishedContent(null); // Reset polished content to prevent showing old data
    setStepProgress({
      'UPLOADED': 0, // Start with 10% progress to show activity
      'ANALYZE JOB DESCRIPTION': 0,
      'ANALYZE RESUME': 0,
      'GENERATE CONTENT': 0,
      'POLISH CONTENT': 0,
      'PREVIEW CONTENT': 0
    });
    let interval;
    
    const checkStatus = async () => {
      // Prevent concurrent API calls
      if (isCheckingStatus) {
        console.log('Status check already in progress, skipping...');
        return;
      }
      
      try {
        setIsCheckingStatus(true);
        console.log('Checking status...');
        const session = await fetchAuthSession();
        const idToken = session.tokens.idToken.toString();

        const response = await fetch(`${process.env.REACT_APP_API_GATEWAY_URL}/improve-resume`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
            'access-control-allow-headers': 'Content-Type,X-Amz-Date,Authorization,X-Api-Key,X-Amz-Security-Token,access-control-allow-headers,access-control-allow-origin,access-control-allow-methods',
            'access-control-allow-origin': '*',
            'access-control-allow-methods': 'OPTIONS,POST'
          },
          body: JSON.stringify({
              operation: 'STATUS_CHECK',
              user_id: userId,
              submission_id: submissionId
          })
        });

        const data = await response.json();
        console.log('Received status:', data.Processing_Status);

        // Handle case where no status is returned (process just started)
        if (!data.Processing_Status || data.Processing_Status === '') {
          console.log('No status returned, assuming process is starting...');
          setStatus('UPLOADED');
          setStepProgress(prev => {
            const newProgress = { ...prev };
            // Increment UPLOADED progress by 10%
            const currentProgress = prev['UPLOADED'] || 0;
            newProgress['UPLOADED'] = Math.min(currentProgress + 10, 90);
            return newProgress;
          });
          return;
        }

        if (data.Processing_Status === 'FAILED') {
          console.log('Process failed');
          setStatus('FAILED');
          setError('We encountered an issue processing your resume.');
          clearInterval(interval);
        } else if (data.Processing_Status === 'COMPLETED') {
          console.log('Process completed, fileKey:', data.Results_Link);
          setStatus('COMPLETED');
          setResultData({
            status: 'COMPLETED',
            fileKey: data.Results_Link
          });
          
          // Set all steps to 100% when completed
          setStepProgress(prev => {
            const newProgress = { ...prev };
            steps.forEach(step => {
              newProgress[step] = 100;
            });
            return newProgress;
          });
          
          clearInterval(interval);
        } else if (data.Processing_Status === 'PREVIEW CONTENT') {
          console.log('Process completed at preview stage');
          setStatus('PREVIEW CONTENT');
          
          // Set all steps to 100% since PREVIEW CONTENT is now the final step
          setStepProgress(prev => {
            const newProgress = { ...prev };
            steps.forEach(step => {
              newProgress[step] = 100;
            });
            return newProgress;
          });

          // Extract polished content from the same response if available
          if (data.polished_content && !polishedContent) {
            console.log('Setting polished content for preview');
            setPolishedContent(data.polished_content);
          }
          
          // Stop polling since this is the final state
          clearInterval(interval);
        } else {
          console.log('Process in progress:', data.Processing_Status);
          setStatus(data.Processing_Status);
          
          // Update progress for steps based on current status
          setStepProgress(prev => {
            const newProgress = { ...prev };
            const currentStepIndex = statusToStepMapping[data.Processing_Status];
            console.log('Current step index:', currentStepIndex);
            
            // Make sure we found the step
            if (currentStepIndex === undefined) {
              console.warn('Unknown status received:', data.Processing_Status);
              // If no status or unknown status, increment UPLOADED stage
              const currentProgress = prev['UPLOADED'] || 0;
              newProgress['UPLOADED'] = Math.min(currentProgress + 10, 90);
              return newProgress;
            }

            // Set progress based on current status
            for (let i = 0; i < steps.length; i++) {
              if (i < currentStepIndex) {
                newProgress[steps[i]] = 100; // Previous steps completed
              } else if (i === currentStepIndex) {
                // Increment current step progress by 10% (max 90%)
                const currentProgress = prev[steps[i]] || 0;
                newProgress[steps[i]] = Math.min(currentProgress + 10, 90);
              } else {
                newProgress[steps[i]] = 0; // Future steps not started
              }
            }
            
            return newProgress;
          });

          // Extract polished content from the same response if status is "POLISH CONTENT"
          if (data.Processing_Status === 'POLISH CONTENT' && data.polished_content && !polishedContent) {
            console.log('Setting polished content from POLISH CONTENT status');
            setPolishedContent(data.polished_content);
          }
        }

      } catch (error) {
        console.error('API Error:', error);
        setApiError(error.message || 'Unknown error occurred');
        clearInterval(interval);
      } finally {
        setIsCheckingStatus(false);
      }
    };

    // Only start polling if we have userId and submissionId, not loading, and not in final states
    if (userId && submissionId && !loading && !userLoading && status !== 'COMPLETED' && status !== 'FAILED') {
      // Set initial progress to show activity immediately
      setStepProgress(prev => ({
        ...prev,
        'UPLOADED': 10 // Start with 10% progress to show activity
      }));
      
      checkStatus(); // Initial check
      interval = setInterval(checkStatus, 20000); // Check every 20 seconds
      window.mainPollingInterval = interval; // Store for later clearing
    }

    return () => {
      if (interval) clearInterval(interval);
      if (window.mainPollingInterval) clearInterval(window.mainPollingInterval);
    };
  }, [userId, submissionId, loading, userLoading]); // Updated dependencies

  const handleDownload = async (fileKey) => {
    try {
      console.log('Starting download with fileKey:', fileKey);
      const session = await fetchAuthSession();
      const idToken = session.tokens.idToken.toString();

      const response = await fetch(`${process.env.REACT_APP_API_GATEWAY_URL}/improve-resume`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
          'access-control-allow-headers': 'Content-Type,X-Amz-Date,Authorization,X-Api-Key,X-Amz-Security-Token,access-control-allow-headers,access-control-allow-origin,access-control-allow-methods',
          'access-control-allow-origin': '*',
          'access-control-allow-methods': 'OPTIONS,POST'
        },
        body: JSON.stringify({
            operation: 'DOWNLOAD',
            user_id: userId,
            submission_id: submissionId,
            fileKey: fileKey
        })
      });

      const data = await response.json();
      console.log('Parsed data:', data);

      if (!data.downloadUrl) {
        throw new Error(data.error || 'Download URL not found in response');
      }

      console.log('Download URL:', data.downloadUrl);
      
      // Create a temporary link and click it to start download
      const link = document.createElement('a');
      link.href = data.downloadUrl;
      link.download = 'improved_resume.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download error:', error);
      setError(`Failed to download file: ${error.message}`);
    }
  };



  const continueToDocument = async () => {
    try {
      console.log('Continuing to document creation...');
      const session = await fetchAuthSession();
      const idToken = session.tokens.idToken.toString();

      const response = await fetch(`${process.env.REACT_APP_API_GATEWAY_URL}/improve-resume`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
          'access-control-allow-headers': 'Content-Type,X-Amz-Date,Authorization,X-Api-Key,X-Amz-Security-Token,access-control-allow-headers,access-control-allow-origin,access-control-allow-methods',
          'access-control-allow-origin': '*',
          'access-control-allow-methods': 'OPTIONS,POST'
        },
        body: JSON.stringify({
            operation: 'CONTINUE_TO_DOCUMENT',
            user_id: userId,
            submission_id: submissionId
        })
      });

      const data = await response.json();
      console.log('Continue to document response:', data);

      if (data.message) {
        setStatus('CREATE DOCUMENT');
        
        // Clear the main polling interval to prevent conflicts
        if (window.mainPollingInterval) {
          clearInterval(window.mainPollingInterval);
          window.mainPollingInterval = null;
        }
        
        // Start polling again for document creation
        let documentInterval; // Declare outside function scope
        
        const checkDocumentStatus = async () => {
          // Prevent concurrent API calls
          if (isCheckingDocumentStatus) {
            console.log('Document status check already in progress, skipping...');
            return;
          }
          
          try {
            setIsCheckingDocumentStatus(true);
            const session = await fetchAuthSession();
            const idToken = session.tokens.idToken.toString();

            const response = await fetch(`${process.env.REACT_APP_API_GATEWAY_URL}/improve-resume`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`,
                'access-control-allow-headers': 'Content-Type,X-Amz-Date,Authorization,X-Api-Key,X-Amz-Security-Token,access-control-allow-headers,access-control-allow-origin,access-control-allow-methods',
                'access-control-allow-origin': '*',
                'access-control-allow-methods': 'OPTIONS,POST'
              },
              body: JSON.stringify({
                  operation: 'STATUS_CHECK',
                  user_id: userId,
                  submission_id: submissionId
              })
            });

            const data = await response.json();
            console.log('Document creation status:', data.Processing_Status);

            if (data.Processing_Status === 'COMPLETED') {
              console.log('Document creation completed, fileKey:', data.Results_Link);
              setStatus('COMPLETED');
              setResultData({
                status: 'COMPLETED',
                fileKey: data.Results_Link
              });
              setStepProgress(prev => {
                const newProgress = { ...prev };
                steps.forEach(step => {
                  newProgress[step] = 100;
                });
                return newProgress;
              });
              if (documentInterval) clearInterval(documentInterval);
            } else if (data.Processing_Status === 'FAILED') {
              console.log('Document creation failed');
              setStatus('FAILED');
              setError('We encountered an issue creating your document.');
              if (documentInterval) clearInterval(documentInterval);
            } else if (data.Processing_Status === 'CREATE DOCUMENT') {
              console.log('Document creation in progress');
              setStatus(data.Processing_Status);
              // Increment CREATE DOCUMENT progress by 10% (max 90%)
              setStepProgress(prev => {
                const currentProgress = prev['CREATE DOCUMENT'] || 0;
                return {
                  ...prev,
                  'CREATE DOCUMENT': Math.min(currentProgress + 10, 90)
                };
              });
            } else {
              console.log('Document creation status:', data.Processing_Status);
              // Keep polling for other statuses
            }
          } catch (error) {
            console.error('API Error during document creation:', error);
            setApiError(error.message || 'Unknown error occurred during document creation');
            if (documentInterval) clearInterval(documentInterval);
          } finally {
            setIsCheckingDocumentStatus(false);
          }
        };

        // Start polling for document creation status
        checkDocumentStatus();
        documentInterval = setInterval(checkDocumentStatus, 20000); // Check every 20 seconds
      } else {
        console.error('Error continuing to document:', data.error);
        setError('Failed to continue to document creation. Please try again.');
      }
    } catch (error) {
      console.error('Error continuing to document:', error);
      setError('Failed to continue to document creation. Please try again.');
    }
  };

  const parseResumeSections = (xmlContent) => {
    try {
      // Extract content between <RESUME> and </RESUME> tags
      const resumeMatch = xmlContent.match(/<RESUME>([\s\S]*?)<\/RESUME>/);
      if (!resumeMatch) return {};

      const resumeContent = resumeMatch[1];
      const sections = {};
      
      // Extract name
      const nameMatch = resumeContent.match(/<NAME>([\s\S]*?)<\/NAME>/);
      if (nameMatch) {
        sections.name = nameMatch[1].trim();
      }

      // Extract contact information
      const contactInfo = [];
      const emailMatch = resumeContent.match(/<EMAIL>([\s\S]*?)<\/EMAIL>/);
      const phoneMatch = resumeContent.match(/<PHONE>([\s\S]*?)<\/PHONE>/);
      const linkedinMatch = resumeContent.match(/<LINKEDIN>([\s\S]*?)<\/LINKEDIN>/);
      const githubMatch = resumeContent.match(/<GITHUB>([\s\S]*?)<\/GITHUB>/);
      const portfolioMatch = resumeContent.match(/<PORTFOLIO>([\s\S]*?)<\/PORTFOLIO>/);

      if (emailMatch) contactInfo.push(`📧 ${emailMatch[1].trim()}`);
      if (phoneMatch) contactInfo.push(`📞 ${phoneMatch[1].trim()}`);
      if (linkedinMatch) contactInfo.push(`🔗 LinkedIn: ${linkedinMatch[1].trim()}`);
      if (githubMatch) contactInfo.push(`💻 GitHub: ${githubMatch[1].trim()}`);
      if (portfolioMatch) contactInfo.push(`🌐 Portfolio: ${portfolioMatch[1].trim()}`);

      if (contactInfo.length > 0) {
        sections.contact = contactInfo.join(' | ');
      }

      // Extract professional summary
      const summaryMatch = resumeContent.match(/<PROFESSIONAL_SUMMARY>([\s\S]*?)<\/PROFESSIONAL_SUMMARY>/);
      if (summaryMatch) {
        sections.summary = summaryMatch[1].trim();
      }

      // Extract skills
      const skillsMatch = resumeContent.match(/<SKILLS>([\s\S]*?)<\/SKILLS>/);
      if (skillsMatch) {
        let skillsContent = '';
        const skillsData = skillsMatch[1];
        const categoryMatches = skillsData.matchAll(/<CATEGORY_(\d+)>([\s\S]*?)<\/CATEGORY_\1>/g);
        const detailMatches = skillsData.matchAll(/<CATEGORY_DETAIL_(\d+)>([\s\S]*?)<\/CATEGORY_DETAIL_\1>/g);
        
        const categories = Array.from(categoryMatches);
        const details = Array.from(detailMatches);
        
        for (let i = 0; i < categories.length; i++) {
          const category = categories[i][2].trim();
          const detail = details[i] ? details[i][2].trim() : '';
          if (category && detail) {
            skillsContent += `• ${category}: ${detail}\n`;
          }
        }
        sections.skills = skillsContent.trim();
      }

      // Extract experience
      const experienceMatch = resumeContent.match(/<EXPERIENCES>([\s\S]*?)<\/EXPERIENCES>/);
      if (experienceMatch) {
        let experienceContent = '';
        const experienceData = experienceMatch[1];
        
        // Extract each experience
        const experienceMatches = experienceData.matchAll(/<EXPERIENCE_(\d+)>([\s\S]*?)<\/EXPERIENCE_\1>/g);
        
        for (const expMatch of experienceMatches) {
          const expContent = expMatch[2];
          const roleMatch = expContent.match(/<ROLE_\d+>([\s\S]*?)<\/ROLE_\d+>/);
          const companyMatch = expContent.match(/<COMPANY_\d+>([\s\S]*?)<\/COMPANY_\d+>/);
          const fromYearMatch = expContent.match(/<FROM_YEAR_\d+>([\s\S]*?)<\/FROM_YEAR_\d+>/);
          const toYearMatch = expContent.match(/<TO_YEAR_\d+>([\s\S]*?)<\/TO_YEAR_\d+>/);
          const locationMatch = expContent.match(/<LOCATION_\d+>([\s\S]*?)<\/LOCATION_\d+>/);
          
          if (roleMatch && companyMatch) {
            const role = roleMatch[1].trim();
            const company = companyMatch[1].trim();
            const fromYear = fromYearMatch ? fromYearMatch[1].trim() : '';
            const toYear = toYearMatch ? toYearMatch[1].trim() : '';
            const location = locationMatch ? locationMatch[1].trim() : '';
            
            experienceContent += `📋 ${role} @ ${company}\n`;
            if (fromYear || toYear || location) {
              const timeLocation = [fromYear, toYear, location].filter(Boolean).join(', ');
              experienceContent += `   ${timeLocation}\n`;
            }
            
            // Extract bullet points
            const bulletMatches = expContent.matchAll(/<R_\d+_BULLET_PT_(\d+)>([\s\S]*?)<\/R_\d+_BULLET_PT_\1>/g);
            for (const bulletMatch of bulletMatches) {
              const bullet = bulletMatch[2].trim();
              if (bullet) {
                experienceContent += `   • ${bullet}\n`;
              }
            }
            experienceContent += '\n';
          }
        }
        sections.experience = experienceContent.trim();
      }

      // Extract education
      const educationMatch = resumeContent.match(/<EDUCATION>([\s\S]*?)<\/EDUCATION>/);
      if (educationMatch) {
        let educationContent = '';
        const educationData = educationMatch[1];
        
        const degreeMatches = educationData.matchAll(/<DEGREE_(\d+)>([\s\S]*?)<\/DEGREE_\1>/g);
        const universityMatches = educationData.matchAll(/<UNIVERSITY_COLLEGE_(\d+)>([\s\S]*?)<\/UNIVERSITY_COLLEGE_\1>/g);
        const yearMatches = educationData.matchAll(/<YEAR_(\d+)>([\s\S]*?)<\/YEAR_\1>/g);
        const gpaMatches = educationData.matchAll(/<GPA_(\d+)>([\s\S]*?)<\/GPA_\1>/g);
        
        const degrees = Array.from(degreeMatches);
        const universities = Array.from(universityMatches);
        const years = Array.from(yearMatches);
        const gpas = Array.from(gpaMatches);
        
        for (let i = 0; i < degrees.length; i++) {
          const degree = degrees[i][2].trim();
          const university = universities[i] ? universities[i][2].trim() : '';
          const year = years[i] ? years[i][2].trim() : '';
          const gpa = gpas[i] ? gpas[i][2].trim() : '';
          
          if (degree) {
            educationContent += `• ${degree}`;
            if (university) educationContent += `, ${university}`;
            if (year) educationContent += ` (${year})`;
            if (gpa) educationContent += ` - GPA: ${gpa}`;
            educationContent += '\n';
          }
        }
        sections.education = educationContent.trim();
      }

      // Extract projects
      const projectsMatch = resumeContent.match(/<PROJECTS>([\s\S]*?)<\/PROJECTS>/);
      if (projectsMatch) {
        let projectsContent = '';
        const projectsData = projectsMatch[1];
        
        const projectMatches = projectsData.matchAll(/<PROJECT_(\d+)>([\s\S]*?)<\/PROJECT_\1>/g);
        
        for (const projMatch of projectMatches) {
          const projContent = projMatch[2];
          
          // The content is already the project name, no need to extract from tags
          const projectName = projContent.trim();
          projectsContent += `📊 ${projectName}\n`;
          
          // Extract bullet points from the original projects content
          const bulletMatches = projectsData.matchAll(/<P\d+_BULLET_PT_(\d+)>([\s\S]*?)<\/P\d+_BULLET_PT_\1>/g);
          for (const bulletMatch of bulletMatches) {
            const bullet = bulletMatch[2].trim();
            if (bullet) {
              projectsContent += `   • ${bullet}\n`;
            }
          }
          projectsContent += '\n';
        }
        sections.projects = projectsContent.trim();
      }

      return sections;
    } catch (error) {
      console.error('Error parsing resume sections:', error);
      return {};
    }
  };

  const extractKeyImprovements = (xmlContent) => {
    try {
      // Extract content between <KEY_IMPROVEMENTS> and </KEY_IMPROVEMENTS> tags
      const improvementsMatch = xmlContent.match(/<KEY_IMPROVEMENTS>([\s\S]*?)<\/KEY_IMPROVEMENTS>/);
      if (!improvementsMatch) return null;

      return improvementsMatch[1].trim();
    } catch (error) {
      console.error('Error extracting key improvements:', error);
      return null;
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Show email at the top
  // const EmailHeader = () => (
  //   <div className="mb-4 text-sm text-gray-600">
  //     Logged in as: {userEmail}
  //   </div>
  // );

  // Error View
  if (error || status === 'FAILED') {
    return (
      <div className="max-w-4xl mx-auto p-6">
        {/* <EmailHeader /> */}
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <p className="text-orange-600 mb-4">
            {error || 'We encountered an issue processing your resume.'}
          </p>
          <p className="text-gray-600 mb-4">
            Our team is here to help. Please reach out to us for assistance.
            {submissionId && (
              <>
                <br /><br />
                <span className="font-medium">Reference ID: {submissionId}</span>
                <br />
                Please share this reference ID with our support team for faster assistance.
              </>
            )}
          </p>
          <a
            href="/contact"
            className="inline-block py-2 px-4 bg-orange-600 text-white rounded-md hover:bg-orange-700"
          >
            Contact Support
          </a>
        </div>
      </div>
    );
  }

  // Processing View
  if (apiError) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        {/* <EmailHeader /> */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">API Error: {apiError}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 py-2 px-4 bg-red-600 text-white rounded-md"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Check if all progress bars are at 100% AND status is actually COMPLETED or PREVIEW CONTENT
  const allStepsComplete = steps.every(step => stepProgress[step] === 100) && (status === 'COMPLETED' || status === 'PREVIEW CONTENT');

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* <EmailHeader /> */}
      <h1 className="text-2xl font-bold mb-6">
        {status === 'PREVIEW CONTENT' ? 'Review New Resume' : 'Resume Status'}
      </h1>
      
      <div className="space-y-6">
        {/* Progress Section - commented out when all steps are complete */}
        {!allStepsComplete && (
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Processing your resume...</h2>
            <div className="space-y-4">
              <ProcessingStep
                step="Files Uploaded"
                duration={100}
                currentTime={stepProgress['UPLOADED']}
                polishedContent={polishedContent}
              />
              <ProcessingStep
                step="Analyzing Job Description"
                duration={100}
                currentTime={stepProgress['ANALYZE JOB DESCRIPTION']}
                polishedContent={polishedContent}
              />
              <ProcessingStep
                step="Analyzing Resume"
                duration={100}
                currentTime={stepProgress['ANALYZE RESUME']}
                polishedContent={polishedContent}
              />
              <ProcessingStep
                step="Generating Content"
                duration={100}
                currentTime={stepProgress['GENERATE CONTENT']}
                polishedContent={polishedContent}
              />
              <ProcessingStep
                step="Polishing Content"
                duration={100}
                currentTime={stepProgress['POLISH CONTENT']}
                polishedContent={polishedContent}
              />
              <ProcessingStep
                step="Previewing Content"
                duration={100}
                currentTime={stepProgress['PREVIEW CONTENT']}
                polishedContent={polishedContent}
              />
              {/* <ProcessingStep
                step="Creating Document"
                duration={100}
                currentTime={stepProgress['CREATE DOCUMENT']}
                polishedContent={polishedContent}
              /> */}
            </div>
          </div>
        )}

        {/* Status Message */}
        {status !== 'PREVIEW CONTENT' && (
          <p className="text-sm text-gray-500 text-center">
            {status === 'UPLOADED' ? 'Starting the process...' :
             status === 'COMPLETED' ? 'Your resume is ready!' :
             'This process typically takes 2-3 minutes'}
          </p>
        )}

        {/* Preview Content Section - show when status is PREVIEW CONTENT */}
        {polishedContent && status === 'PREVIEW CONTENT' && (
          <>
            <div className="space-y-4 mt-6">
              
              {(() => {
                const sections = parseResumeSections(polishedContent);
                return (
                  <>
                    {/* Temporarily hidden - Name section not populating correctly
                    {sections.name && (
                      <div className="bg-white shadow rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <h3 className="text-lg font-medium">Name</h3>
                          <button className="text-gray-400 hover:text-gray-600">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                        </div>
                        <div className="bg-gray-50 rounded p-3">
                          <p className="text-gray-800">📝 {sections.name}</p>
                        </div>
                      </div>
                    )}

                    Temporarily hidden - Contact section not populating correctly
                    {sections.contact && (
                      <div className="bg-white shadow rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <h3 className="text-lg font-medium">Contact Information</h3>
                          <button className="text-gray-400 hover:text-gray-600">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                        </div>
                        <div className="bg-gray-50 rounded p-3">
                          <p className="text-gray-800">{sections.contact}</p>
                        </div>
                      </div>
                    )}
                    */}

                    {sections.summary && (
                      <div className="bg-white shadow rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <h3 className="text-lg font-medium">Professional Summary</h3>
                          <div className="relative group">
                            <button className="text-gray-400 hover:text-gray-600">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            </button>
                            <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 whitespace-nowrap">
                              Feature coming soon
                              <div className="absolute top-full right-2 border-4 border-transparent border-t-gray-800"></div>
                            </div>
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded p-3">
                          <pre className="whitespace-pre-wrap text-sm text-gray-800">{sections.summary}</pre>
                        </div>
                      </div>
                    )}

                    {sections.skills && (
                      <div className="bg-white shadow rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <h3 className="text-lg font-medium">Skills</h3>
                          <div className="relative group">
                            <button className="text-gray-400 hover:text-gray-600">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            </button>
                            <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 whitespace-nowrap">
                              Feature coming soon
                              <div className="absolute top-full right-2 border-4 border-transparent border-t-gray-800"></div>
                            </div>
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded p-3">
                          <pre className="whitespace-pre-wrap text-sm text-gray-800">{sections.skills}</pre>
                        </div>
                      </div>
                    )}

                    {sections.experience && (
                      <div className="bg-white shadow rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <h3 className="text-lg font-medium">Experience</h3>
                          <div className="relative group">
                            <button className="text-gray-400 hover:text-gray-600">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            </button>
                            <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 whitespace-nowrap">
                              Feature coming soon
                              <div className="absolute top-full right-2 border-4 border-transparent border-t-gray-800"></div>
                            </div>
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded p-3">
                          <pre className="whitespace-pre-wrap text-sm text-gray-800">{sections.experience}</pre>
                        </div>
                      </div>
                    )}

                    {sections.education && (
                      <div className="bg-white shadow rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <h3 className="text-lg font-medium">Education</h3>
                          <div className="relative group">
                            <button className="text-gray-400 hover:text-gray-600">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            </button>
                            <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 whitespace-nowrap">
                              Feature coming soon
                              <div className="absolute top-full right-2 border-4 border-transparent border-t-gray-800"></div>
                            </div>
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded p-3">
                          <pre className="whitespace-pre-wrap text-sm text-gray-800">{sections.education}</pre>
                        </div>
                      </div>
                    )}

                    {sections.projects && (
                      <div className="bg-white shadow rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <h3 className="text-lg font-medium">Projects</h3>
                          <div className="relative group">
                            <button className="text-gray-400 hover:text-gray-600">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            </button>
                            <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 whitespace-nowrap">
                              Feature coming soon
                              <div className="absolute top-full right-2 border-4 border-transparent border-t-gray-800"></div>
                            </div>
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded p-3">
                          <pre className="whitespace-pre-wrap text-sm text-gray-800">{sections.projects}</pre>
                        </div>
                      </div>
                    )}
                  </>
                );
                             })()}
              
              <div className="mt-4 flex justify-center">
                <button
                  disabled
                  className="py-2 px-4 bg-gray-400 text-gray-600 rounded-md cursor-not-allowed opacity-50"
                >
                  Create Document - Coming Soon
                </button>
              </div>
            </div>

            {/* Key Improvements Section */}
            {extractKeyImprovements(polishedContent) && (
              <div className="bg-white shadow rounded-lg p-6 mt-4">
                <h2 className="text-xl font-semibold mb-4">Following improvements were made to your resume</h2>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono">
                    {extractKeyImprovements(polishedContent)}
                  </pre>
                </div>
              </div>
            )}
          </>
        )}

        {/* Show completed status at bottom when processing is done */}
        {/* {status === 'COMPLETED' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mt-6">
            <h2 className="text-xl text-green-600 font-semibold mb-4">Your resume is ready!</h2>
            <button
              onClick={() => {
                console.log('Clicking download with fileKey:', resultData.fileKey);
                handleDownload(resultData.fileKey);
              }}
              className="py-2 px-4 bg-green-600 text-white rounded-md hover:bg-green-700"
              disabled={!resultData.fileKey}
            >
              Download Resume {resultData.fileKey ? '(Ready)' : '(Waiting...)'}
            </button>
          </div>
        )} */}
      </div>
    </div>
  );
};

// Helper component for processing steps
const ProcessingStep = ({ step, duration, currentTime, polishedContent }) => {
  const progress = Math.min(100, (currentTime / duration) * 100);
  const isActive = currentTime > 0;
  const isComplete = progress >= 100;
  
  // Hide the progress bar for "Previewing Content" when content is available
  if (step === "Previewing Content" && polishedContent) {
    return null;
  }

  return (
    <div className="relative">
      <div className="flex items-center mb-2">
        <div className={`w-4 h-4 rounded-full mr-2 ${
          isComplete ? 'bg-green-500' : isActive ? 'bg-blue-500' : 'bg-gray-200'
        }`} />
        <span className={`font-medium ${
          isComplete ? 'text-green-500' : isActive ? 'text-blue-500' : 'text-gray-400'
        }`}>
          {step}
        </span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full ${isComplete ? 'bg-green-500' : 'bg-blue-500'}`}
          style={{ width: `${progress}%`, transition: 'width 0.5s ease-in-out' }}
        />
      </div>
    </div>
  );
};

export default Results; 